import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:disabled:opacity-40",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",

        // App-level action variants — shared by every feature screen through
        // <AppButton />. Kept here so sizing and focus handling stay in one place.
        primary:
          "min-w-28 bg-primaryAlt/90 text-white hover:bg-primaryAlt/80 hover:text-white hover:shadow active:bg-primaryAlt/80",
        secondaryAction:
          "min-w-28 bg-secondaryAlt/90 text-white hover:bg-secondaryAlt/80 hover:text-white active:bg-secondaryAlt/80",
        danger:
          "min-w-28 bg-bsDanger text-white hover:bg-bsDanger/90 hover:text-white hover:shadow active:bg-bsDanger/80",
        search:
          "ml-auto flex w-full gap-1 rounded-lg bg-secondaryAlt/90 text-base uppercase text-white hover:bg-secondaryAlt/80 hover:text-white active:bg-secondaryAlt/80",
        "outline-primary":
          "min-w-28 shrink-0 gap-2 border border-primaryAlt bg-card text-primaryAlt hover:bg-primaryAlt/10 hover:text-primaryAlt active:scale-[0.98] dark:hover:bg-primaryAlt/15",
        "outline-secondary":
          "min-w-28 shrink-0 gap-2 border border-secondaryAlt bg-card text-secondaryAlt hover:bg-secondaryAlt/10 hover:text-secondaryAlt active:scale-[0.98] dark:hover:bg-secondaryAlt/15",
      },
      size: {
        default: "h-10 px-4 py-2",
        // Standard height for app action buttons (primary/secondary/danger/outline-*)
        action: "h-9 px-4 py-2",
        // Matches the h-11 inputs inside .search-panel
        search: "h-11 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
